import { useState } from 'react';
import { BUDGETS } from '../content/faq';
import site from '../content/site';
import Button from './Button';

// Set REACT_APP_FORM_ENDPOINT (Formspree, a serverless function, a CRM webhook) to deliver briefs.
// Without it the form validates and shows the success state only.
const ENDPOINT = process.env.REACT_APP_FORM_ENDPOINT || '';
const EMPTY = { name: '', email: '', company: '', need: '', budget: BUDGETS[1] };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactForm() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = 'Please tell us your name.';
    if (!EMAIL_RE.test(form.email.trim())) er.email = 'Enter a valid work email.';
    if (form.need.trim().length < 10) er.need = 'A sentence or two helps us reply with something useful.';
    return er;
  };

  const submit = async e => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length) return;
    setStatus('sending');
    try {
      if (ENDPOINT) {
        const res = await fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...form, source: 'isl-technologies.com' }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } else {
        await new Promise(r => setTimeout(r, 600));
      }
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  const reset = () => { setForm(EMPTY); setErrors({}); setStatus('idle'); };

  if (status === 'sent') {
    const first = (form.name.trim() || 'there').split(' ')[0];
    return (
      <div role="status" className="py-6">
        <div aria-hidden="true" className="h-12 w-12 bg-accent" />
        <h3 className="mt-6 text-[32px] tracking-[-0.03em]">Thanks, {first}. Brief received.</h3>
        <p className="mt-2.5">An engineer will reply to {form.email} within one business day.</p>
        <Button variant="outline-ink" size={48} arrow={false} className="mt-6" onClick={reset}>Send another</Button>
      </div>
    );
  }

  const field = (key, label, extra = {}) => (
    <label className={`field ${extra.full ? 'col-span-full' : ''}`}>
      {label}
      <input
        className="input" name={key} value={form[key]} onChange={set(key)}
        type={extra.type || 'text'} autoComplete={extra.autoComplete} required={extra.required}
        aria-invalid={errors[key] ? 'true' : undefined} aria-describedby={errors[key] ? `${key}-error` : undefined}
      />
      {errors[key] && <span id={`${key}-error`} role="alert" className="text-accent-700">{errors[key]}</span>}
    </label>
  );

  return (
    <form noValidate onSubmit={submit} className="grid gap-[18px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))' }}>
      {field('name', 'Name', { autoComplete: 'name', required: true })}
      {field('email', 'Work email', { type: 'email', autoComplete: 'email', required: true })}
      {field('company', 'Company', { autoComplete: 'organization', full: true })}

      <label className="field col-span-full">
        What do you need?
        <textarea
          className="input" name="need" rows={4} value={form.need} onChange={set('need')} required
          placeholder="The product, the problem, any deadlines."
          aria-invalid={errors.need ? 'true' : undefined} aria-describedby={errors.need ? 'need-error' : undefined}
        />
        {errors.need && <span id="need-error" role="alert" className="text-accent-700">{errors.need}</span>}
      </label>

      <fieldset className="col-span-full">
        <legend className="mb-1.5 text-[13px] font-semibold">Budget range</legend>
        <div className="grid border-1 border-divider" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))' }}>
          {BUDGETS.map(b => {
            const on = form.budget === b;
            return (
              <button
                key={b} type="button" aria-pressed={on} onClick={() => setForm(f => ({ ...f, budget: b }))}
                className={`min-h-[48px] border-r-1 border-divider px-3 text-left text-[14px] font-semibold last:border-r-0 ${on ? 'bg-accent text-bg' : 'bg-bg text-ink'}`}
              >
                {b}
              </button>
            );
          })}
        </div>
      </fieldset>

      <button
        type="submit" disabled={status === 'sending'}
        className="col-span-full mt-1.5 flex h-btn items-center justify-between bg-accent px-5 text-[17px] font-extrabold text-bg transition-colors hover:bg-accent-600 disabled:cursor-wait disabled:opacity-70"
      >
        {status === 'sending' ? 'Sending…' : 'Send project brief'} <span aria-hidden="true">→</span>
      </button>

      {status === 'error' && (
        <p role="alert" className="col-span-full text-[13px] font-semibold text-accent-700">
          Something went wrong sending the brief. Email us at <a href={`mailto:${site.email}`} className="underline">{site.email}</a> instead.
        </p>
      )}
      <p className="col-span-full text-[13px] text-neutral-700">Happy to sign an NDA before we get into details.</p>
    </form>
  );
}
